import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import CategoryCards from '../components/CategoryCards';
import { FiFilter, FiX, FiGrid, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { optimizedAPI } from '../lib/optimizedApi';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';

export default function Collections() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showCategories, setShowCategories] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    price_min: '',
    price_max: '',
    search: '',
    sort: 'date',
  });
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const [searchParams] = useSearchParams();
  const perPage = 20; // 20 products per page

  useEffect(() => {
    const category = searchParams.get('category') || '';
    const search = searchParams.get('search') || '';
    
    setFilters(prev => ({
      ...prev,
      category,
      search,
    }));
    setCurrentPage(1);
    setProducts([]);
  }, [searchParams]);


  useEffect(() => {
    fetchProducts();
    // Track collection page view
    trackEvent('collection_view', 'browsing', 'collections_page');
  }, [filters, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
      setLoading(true);

    try {
      // Use optimized API for collections
      const apiParams = {
        per_page: perPage,
        page: currentPage,
        search: filters.search || undefined,
        min_price: filters.price_min || undefined,
        max_price: filters.price_max || undefined,
        sort: filters.sort
      };

      // Add category filter if provided
      if (filters.category) {
        apiParams.category = filters.category;
      }

      // Use optimized API for collections
      const response = await optimizedAPI.getCollectionsProducts(apiParams);
      const fetchedProducts = response.products || [];
      
        setProducts(fetchedProducts);
      
      // Get total pages from response pagination object
      if (response.pagination && response.pagination.total_pages) {
        setTotalPages(response.pagination.total_pages);
      } else if (response.total_pages) {
        // Fallback for direct total_pages (if not nested)
        setTotalPages(response.total_pages);
      } else if (response.has_more === false || (response.pagination && response.pagination.has_more === false)) {
        setTotalPages(currentPage);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
        setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    // Clear existing search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // If search is being changed, debounce it
    if (newFilters.hasOwnProperty('search')) {
      const timeout = setTimeout(() => {
        setFilters(prev => ({ ...prev, ...newFilters }));
      }, 500); // 500ms debounce
      setSearchTimeout(timeout);
    } else {
      // For other filters, update immediately
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
  };

  const handleCategorySelect = (categorySlug) => {
    // Redirect to category-specific page
    window.location.href = `/collections/${categorySlug}`;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <>
      <SEO
        title="Collections - Premium Ballerina Dresses & Jewelry | Bailebelle"
        description="Browse our complete collection of premium ballerina dresses, ballet jewelry, and accessories. Discover timeless elegance and perfect fit."
        keywords="ballerina dresses collection, ballet jewelry, dress, ballet costumes, ballerina outfits"
        url="/collections"
      />
      <div className="min-h-screen bg-black">
      {/* Show Category Cards First */}
      {showCategories && !filters.category && !filters.search ? (
        <CategoryCards onCategorySelect={handleCategorySelect} />
      ) : (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl md:text-2xl font-bold text-white">
                {filters.search ? `Search Results for "${filters.search}"` : 
                 filters.category ? `${filters.category.charAt(0).toUpperCase() + filters.category.slice(1).replace('-', ' ')} Collection` : 
                 'All Collections'}
              </h1>
              
              {/* Back to Categories Button */}
              {filters.category && (
                <button
                  onClick={() => {
                    setShowCategories(true);
                    setFilters(prev => ({ ...prev, category: '' }));
                    setProducts([]);
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-white font-semibold rounded-lg transition-colors border border-gray-600 hover:border-pink-500 text-sm"
                >
                  <FiGrid className="mr-1.5" size={14} />
                  Browse Categories
                </button>
              )}
            </div>
            <p className="text-gray-400 text-sm">
              {filters.category ? 
                `Discover our ${filters.category.replace('-', ' ')} collection` :
                'Discover our complete collection of ballerina dresses and jewelry'
              }
            </p>
          </div>

          {/* Top Filter Bar */}
          <div className="mb-6">
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                {/* Filter Toggle Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-2 text-white font-semibold rounded-lg transition-colors bg-gray-800 hover:bg-gray-700 border border-gray-600 text-sm"
                >
                  {showFilters ? <FiX className="mr-1.5" size={16} /> : <FiFilter className="mr-1.5" size={16} />}
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>

                {/* Quick Filter Options */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleFilterChange({ sort: 'date' })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.sort === 'date' 
                        ? 'bg-pink-600 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Newest
                  </button>
                  <button
                    onClick={() => handleFilterChange({ sort: 'price' })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.sort === 'price' 
                        ? 'bg-pink-600 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Price
                  </button>
                  <button
                    onClick={() => handleFilterChange({ sort: 'popularity' })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.sort === 'popularity' 
                        ? 'bg-pink-600 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Popular
                  </button>
                </div>
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-1.5 text-sm">Price Range</label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.price_min}
                          onChange={(e) => handleFilterChange({ price_min: e.target.value })}
                          className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.price_max}
                          onChange={(e) => handleFilterChange({ price_max: e.target.value })}
                          className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-1.5 text-sm">Search</label>
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange({ search: e.target.value })}
                        className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setFilters({
                          category: '',
                          price_min: '',
                          price_max: '',
                          search: '',
                          sort: 'date',
                        })}
                        className="w-full px-3 py-1.5 text-white font-semibold rounded-lg transition-colors border border-gray-600 hover:border-pink-500 text-sm"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="w-full">
            {loading ? (
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[...Array(10)].map((_, index) => (
                    <div key={index} className="bg-gray-900 rounded-xl overflow-hidden animate-pulse flex flex-col">
                      <div className="bg-gray-800 flex-1" style={{ height: '250px' }}></div>
                      <div className="p-3 bg-gray-900 flex-shrink-0" style={{ minHeight: '80px' }}>
                        <div className="space-y-2">
                          <div className="bg-gray-800 h-4 rounded w-3/4"></div>
                          <div className="bg-gray-800 h-5 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 text-sm mb-4">
                  No products found matching your criteria.
                </div>
                <button
                  onClick={() => setFilters({
                    category: '',
                    price_min: '',
                    price_max: '',
                    search: '',
                    sort: 'date',
                  })}
                  className="px-4 py-2 text-white font-semibold rounded-lg transition-colors text-sm"
                  style={{ backgroundColor: '#CF0F63' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#B00D56'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#CF0F63'}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                <div className="w-full">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-12 gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center ${
                        currentPage === 1
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 text-white hover:bg-gray-700'
                      }`}
                    >
                      <FiChevronLeft className="mr-1" size={18} />
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex gap-2">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        
                        // Show first page, last page, current page, and pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-pink-600 text-white'
                                  : 'bg-gray-800 text-white hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return (
                            <span key={pageNum} className="px-2 py-2 text-gray-600">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center ${
                        currentPage === totalPages
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 text-white hover:bg-gray-700'
                      }`}
                    >
                      Next
                      <FiChevronRight className="ml-1" size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
}
